import { IncomingMessage, ServerResponse } from 'http';
import { setCookie, getCookie, getCookieFromResponse } from '~/src/helpers/cookie';
import { COOKIE_DOMAIN } from '~/src/constants/index';
import { ABNTest, ABNTestVariant } from '~/src/api/content/geography/get-all-cities';

export const ABN_TEST_COOKIE = 'abn_test';
export const ABN_VARINAT_COOKIE = 'abn_variant';
export const ABN_DEFAULT_VARINAT_COOKIE = 'abn_default_variant';

const SEC_IN_YEAR = 31536000;

const SEC_IN_DAY = 86400;

const cookieConfig = {
    maxAge: SEC_IN_YEAR,
    domain: COOKIE_DOMAIN,
    path: '/',
};

export const  getRandomInRange = (min: number, max: number): number => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min)) + min;
};

// Возвращает рандомное значение из cookie, если такого нет генерирует и записывает в куку, обновляет куку с рандомным значением раз в сутки
const getValueFromTestCookie = (req?: IncomingMessage, res?: ServerResponse): number => {
    let testCookie = getCookie(ABN_TEST_COOKIE, req); // 1616591632670.86 - метка времени и рандомная величина

    if (!testCookie) {
        testCookie = getCookieFromResponse(ABN_TEST_COOKIE, res);
    }

    const values = testCookie?.split('.') || [];

    const [timeMarker, value] = values;

    let randomValue: number | undefined = +value;

    const dayNow = Date.now();
    const restTime = +timeMarker + (SEC_IN_DAY * 1000) - dayNow;

    let update = false;

    if (!randomValue) {
        randomValue = getRandomInRange(0, 100);
        update = true;

    } else if (restTime < 0) {
        update = true;
    }

    if (update) {
        setCookie({
            cookieName: ABN_TEST_COOKIE,
            cookieValue: `${dayNow}.${randomValue}`,
            cookieConfig,
            res,
        });
    }

    return randomValue;
};

const getVariantByValue = (variants: ABNTest['variants'], randomValue: number) => {
    for (const variant of variants) {
        randomValue -= Number(variant.traffic);

        if (randomValue <= 0) {
            return variant;
        }
    }

    return null;
};

const setABNVariantsCookie = (abnTests: ABNTest[], randomValue: number, res?: ServerResponse) => {
    const valuesStr = abnTests.reduce((acc: string[], currTest) => {
        if (currTest.variants.length) {
            const selectVariant = getVariantByValue(currTest.variants, randomValue);
            acc.push(`${currTest.name}:${selectVariant?.label}`);
        } else {
            acc.push(`${currTest.name}`);
        }

        return acc;
    }, []);

    setCookie({
        cookieName: ABN_VARINAT_COOKIE,
        cookieValue: JSON.stringify(valuesStr),
        cookieConfig,
        res,
    });
};

/**
 * Возвращает дефолтный вариант из куки 'abn_default_variant', если установлена.
 * Кука 'abn_default_variant' должна иметь формат типа: { 'showing_menu': 'menu_new' }
 */
const getDefaultVariantFromCookie = (testName: string, req?: IncomingMessage) => {
    const defaultVariantsCookie = getCookie(ABN_DEFAULT_VARINAT_COOKIE, req);

    try {
        const defaultVariants: Record<string, string> | undefined = defaultVariantsCookie ? JSON.parse(defaultVariantsCookie) : null;
        return defaultVariants ? defaultVariants[testName] : null;
    } catch {
        return null;
    }
};

interface IGetVariantProps {
    abnTests: ABNTest[],
    testName: string,
    req?: IncomingMessage,
    res?: ServerResponse,
}

// Получить ABN test по имени
export const getABNTest = (abnTests: ABNTest[], testName: string): ABNTest | undefined => {
    return abnTests?.find((test) => test.name === testName);
};

// Если вариант запрашивается на стороне сервера нужно передать req и res.
export const getABNVariant = ({ abnTests, testName, req, res }: IGetVariantProps): ABNTestVariant | null => {
    const test = abnTests?.find((test) => test.name === testName);

    if (!test) {
        return null;
    }

    /* Проверка есть ли дефолтный вариант */
    const defaultVariant = getDefaultVariantFromCookie(testName, req);

    if (defaultVariant) {
        return { id: 0, abn_test_id: 0, label: defaultVariant, traffic: 100 };
    }

    const randomValue = getValueFromTestCookie(req, res);

    const { variants } = test;

    setABNVariantsCookie(abnTests, randomValue, res);

    return getVariantByValue(variants, randomValue);
};
